import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Unlock from './pages/Unlock';
import Settings from './pages/Settings';
import Companies from './pages/Companies';
import LoadingSpinner from './components/Common/LoadingSpinner';

// New consolidated pages
import Overview from './pages/Overview';
import Configuration from './pages/Configuration';
import AIFeatureConfiguration from './pages/AIFeatureConfiguration';
import PromotionalTiers from './pages/PromotionalTiers';
import Maintenance from './pages/Maintenance';
import Notifications from './pages/Notifications';
import TierUpgrades from './pages/TierUpgrades';
import VoiceCommandFailures from './pages/VoiceCommandFailures';
import CrashReports from './pages/CrashReports';
import StorageAnalytics from './pages/StorageAnalytics';
import PricingConfiguration from './pages/PricingConfiguration';
import DemoSettings from './pages/DemoSettings';
import Subdomains from './pages/Subdomains';

// Note: Legacy pages (Dashboard, Analytics, Reports, AppConfiguration, FeaturePricing, SubscriptionFeatures)
// are no longer imported - their routes now redirect to consolidated pages

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route Component (redirects to overview if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/overview" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />

          {/* Unlock Route - Public, no redirect if authenticated */}
          <Route
            path="/unlock/:token"
            element={<Unlock />}
          />

          {/* Protected Routes */}
          <Route 
            path="/*" 
            element={
              <ProtectedRoute>
                <Layout>
                  <Routes>
                    {/* New consolidated routes */}
                    <Route path="/overview" element={<Overview />} />
                    <Route path="/companies" element={<Companies />} />
                    <Route path="/storage-analytics" element={<StorageAnalytics />} />
                    <Route path="/pricing-configuration" element={<PricingConfiguration />} />
                    <Route path="/configuration" element={<Configuration />} />
                    <Route path="/ai-management" element={<AIFeatureConfiguration />} />
                    <Route path="/promotions" element={<PromotionalTiers />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/tier-upgrades" element={<TierUpgrades />} />
                    <Route path="/voice-failures" element={<VoiceCommandFailures />} />
                    <Route path="/crash-reports" element={<CrashReports />} />
                    <Route path="/system" element={<Maintenance />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/subdomains" element={<Subdomains />} />
                    <Route path="/demo-settings" element={<DemoSettings />} />

                    {/* Legacy routes - redirect to new consolidated pages */}
                    <Route path="/dashboard" element={<Navigate to="/overview" replace />} />
                    <Route path="/analytics" element={<Navigate to="/overview" replace />} />
                    <Route path="/reports" element={<Navigate to="/overview" replace />} />
                    <Route path="/app-config" element={<Navigate to="/configuration" replace />} />
                    <Route path="/ai-config" element={<Navigate to="/ai-management" replace />} />
                    <Route path="/feature-pricing" element={<Navigate to="/configuration" replace />} />
                    <Route path="/promotional-tiers" element={<Navigate to="/promotions" replace />} />
                    <Route path="/subscription-features" element={<Navigate to="/configuration" replace />} />
                    <Route path="/maintenance" element={<Navigate to="/system" replace />} />

                    {/* Default routes */}
                    <Route path="/" element={<Navigate to="/overview" replace />} />
                    <Route path="*" element={<Navigate to="/overview" replace />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            } 
          />
        </Routes>
      </Box>
    </AuthProvider>
  );
}

export default App;
